import { useMemo } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';

interface VideoPlayerModalProps {
  isOpen: boolean;
  onClose: () => void;
  videoUrl: string;
  videoTitle: string;
  embedCode?: string | null;
}

export function VideoPlayerModal({ isOpen, onClose, videoUrl, videoTitle, embedCode }: VideoPlayerModalProps) {
  const extractIframeAttributes = (iframeCode: string) => {
    const srcMatch = iframeCode.match(/src=["']([^"']+)["']/);
    const allowMatch = iframeCode.match(/allow=["']([^"']+)["']/);

    let src = '';
    if (srcMatch && srcMatch[1]) {
      src = srcMatch[1].replace(/&amp;/g, '&');
    }

    let allow = 'autoplay; fullscreen; picture-in-picture; clipboard-write';
    if (allowMatch && allowMatch[1]) {
      allow = allowMatch[1];
    }

    return { src, allow };
  };

  const extractVimeoId = (url: string): string | null => {
    const patterns = [
      /vimeo\.com\/(\d+)/,
      /player\.vimeo\.com\/video\/(\d+)/,
      /vimeo\.com\/video\/(\d+)/,
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }

    return null;
  };

  const getVimeoEmbedUrl = (url: string): string => {
    const vimeoId = extractVimeoId(url);

    if (vimeoId) {
      const embedParams = new URLSearchParams({
        autoplay: '0',
        title: '0',
        byline: '0',
        portrait: '0',
        dnt: '1',
        controls: '1',
        playsinline: '1',
        quality: 'auto',
        // Parámetros adicionales de seguridad de Vimeo
        logo: '0',              // Oculta el logo de Vimeo
        autopause: '1',         // Pausa cuando pierde foco
        transparent: '0',       // Fondo sólido
      });

      return `https://player.vimeo.com/video/${vimeoId}?${embedParams.toString()}`;
    }

    return url;
  };

  const iframeProps = useMemo(() => {
    // SIEMPRE extraer el ID de Vimeo y construir URL con parámetros actualizados
    // Esto ignora cualquier configuración antigua guardada en embed_code
    let vimeoIdToUse: string | null = null;

    // Primero intentar extraer ID del embed_code si existe
    if (embedCode && embedCode.trim() && embedCode.includes('<iframe')) {
      const { src } = extractIframeAttributes(embedCode);
      if (src) {
        vimeoIdToUse = extractVimeoId(src);
      }
    }

    // Si no se pudo extraer del embed_code, intentar del videoUrl
    if (!vimeoIdToUse) {
      vimeoIdToUse = extractVimeoId(videoUrl);
    }

    // Construir URL con parámetros ACTUALIZADOS de Vimeo
    if (vimeoIdToUse) {
      // Buscar hash de privacidad en CUALQUIER parte del embed_code o videoUrl
      let privacyHash = '';

      // Intentar extraer de embed_code
      if (embedCode) {
        const hashMatch = embedCode.match(/[?&]h=([a-zA-Z0-9]+)/);
        if (hashMatch) {
          privacyHash = hashMatch[1];
        }
      }

      // Si no se encontró en embed_code, intentar en videoUrl
      if (!privacyHash && videoUrl) {
        const hashMatch = videoUrl.match(/[?&]h=([a-zA-Z0-9]+)/);
        if (hashMatch) {
          privacyHash = hashMatch[1];
        }
      }

      // Construir URL base con o sin hash
      let baseUrl = `https://player.vimeo.com/video/${vimeoIdToUse}`;
      const params: Record<string, string> = {};

      // Si hay hash de privacidad, agregarlo primero
      if (privacyHash) {
        params.h = privacyHash;
      }

      // Parámetros de configuración de Vimeo (orden específico)
      Object.assign(params, {
        // Ocultar elementos de interfaz (funcionan sin hash)
        title: '0',
        byline: '0',
        portrait: '0',
        badge: '0',

        // Comportamiento del reproductor
        autopause: '0',
        player_id: '0',
        app_id: '58479',

        // Parámetros de privacidad (requieren hash o cuenta premium)
        controls: '1',
        dnt: '1',
        playsinline: '1',
        quality: 'auto',
        transparent: '0',
      });

      // Construir query string manualmente para mantener el orden
      const queryParts = Object.entries(params).map(([key, value]) => `${key}=${value}`);
      const queryString = queryParts.join('&');

      // CRÍTICO: Cache buster para forzar recarga y obtener configuración actualizada de Vimeo
      const cacheBuster = Date.now();

      return {
        src: `${baseUrl}?${queryString}&_=${cacheBuster}`,
        allow: 'autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share',
      };
    }

    // Fallback a la URL original si no se pudo extraer ID
    return {
      src: videoUrl,
      allow: 'autoplay; fullscreen; picture-in-picture',
    };
  }, [embedCode, videoUrl]);

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={videoTitle}
      description="Reproduciendo video"
      size="full"
      bodyClassName="!p-0"
      footer={
        <div className="flex items-center justify-between gap-4 w-full">
          <p className="text-sm text-subtle">Video protegido — no se permite la descarga.</p>
          <Button variant="glass" onClick={onClose}>Cerrar</Button>
        </div>
      }
    >
      <div className="relative w-full bg-black" style={{ paddingBottom: '56.25%' }}>
        <iframe
          src={iframeProps.src}
          className="absolute top-0 left-0 w-full h-full"
          style={{ border: 'none' }}
          allow={iframeProps.allow}
          allowFullScreen
          title={videoTitle}
          loading="lazy"
        />
      </div>
    </Modal>
  );
}
